import React from "react";
import {
  Card, CardContent, Box, Typography, Chip, Grid, IconButton,
  Tooltip, LinearProgress, CircularProgress, Stack, Divider
} from "@mui/material";
import {
  Visibility, Edit, Delete, Send, Campaign as CampaignIcon,
  TrendingUp, CheckCircle, Schedule
} from "@mui/icons-material";
import { calcRate, statusColors, typeColors } from "../hooks/useCampaign";

// Stat Card Component
export const StatCard = ({ label, value, icon: Icon, className }) => (
  <Card className={`stat-card ${className || ""}`}>
    <CardContent>
      <Box className="stat-content">
        <Box>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
          <Typography variant="h4" className="stat-value">{value}</Typography>
        </Box>
        <Icon className="stat-icon" />
      </Box>
    </CardContent>
  </Card>
);

// Stats Grid
export const StatsGrid = ({ stats }) => (
  <Grid container spacing={2} sx={{ mb: 3 }}>
    <Grid item xs={6} md={3}><StatCard label="Total" value={stats.total} icon={CampaignIcon} /></Grid>
    <Grid item xs={6} md={3}><StatCard label="Running" value={stats.running} icon={TrendingUp} /></Grid>
    <Grid item xs={6} md={3}><StatCard label="Completed" value={stats.completed} icon={CheckCircle} /></Grid>
    <Grid item xs={6} md={3}><StatCard label="Drafts" value={stats.draft} icon={Schedule} /></Grid>
  </Grid>
);

// Campaign Card
export const CampaignCard = ({ campaign, onView, onEdit, onExecute, onDelete }) => {
  const rate = calcRate(campaign.stats?.totalOpened, campaign.stats?.totalSent);
  // Use totalTargeted after execution, fall back to estimatedReach for drafts
  const targetCount = campaign.stats?.totalTargeted || campaign.targetAudience?.estimatedReach || 0;

  return (
    <Card className="campaign-card">
      <CardContent>
        <Box className="campaign-card-header">
          <Typography variant="h6" className="campaign-card-title">{campaign.name}</Typography>
          {campaign.description && (
            <Typography variant="body2" color="text.secondary" className="campaign-description">
              {campaign.description.length > 80 ? `${campaign.description.slice(0, 80)}...` : campaign.description}
            </Typography>
          )}
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Chip label={campaign.type.toUpperCase()} color={typeColors[campaign.type]} size="small" />
            <Chip label={campaign.status.toUpperCase()} color={statusColors[campaign.status]} size="small" />
          </Stack>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2} sx={{ mb: 1 }}>
          {[
            ["Target", targetCount],
            ["Sent", campaign.stats?.totalSent || 0],
            ["Opened", campaign.stats?.totalOpened || 0],
            ["Rate", `${rate}%`]
          ].map(([label, val]) => (
            <Grid item xs={3} key={label}>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
              <Typography variant="body2" fontWeight={600}>{val.toLocaleString?.() || val}</Typography>
            </Grid>
          ))}
        </Grid>

        {campaign.stats?.totalSent > 0 && (
          <LinearProgress variant="determinate" value={Math.min(rate, 100)} sx={{ height: 4, borderRadius: 2 }} />
        )}

        <Box className="campaign-card-footer">
          <Typography variant="caption" color="text.secondary">
            {new Date(campaign.createdAt).toLocaleDateString("en-US", { timeZone: "Asia/Dhaka" })}
          </Typography>
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="View"><IconButton size="small" onClick={() => onView(campaign._id)}><Visibility fontSize="small" /></IconButton></Tooltip>
            {campaign.status === "draft" && (
              <>
                <Tooltip title="Edit"><IconButton size="small" onClick={() => onEdit(campaign._id)}><Edit fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Execute"><IconButton size="small" color="success" onClick={() => onExecute(campaign._id)}><Send fontSize="small" /></IconButton></Tooltip>
              </>
            )}
            {campaign.status !== "running" && (
              <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => onDelete(campaign._id)}><Delete fontSize="small" /></IconButton></Tooltip>
            )}
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

// Campaign List Item (for list view)
export const CampaignListItem = ({ campaign, onView, onEdit, onExecute, onDelete }) => {
  const rate = calcRate(campaign.stats?.totalOpened, campaign.stats?.totalSent);
  const targetCount = campaign.stats?.totalTargeted || campaign.targetAudience?.estimatedReach || 0;

  return (
    <Card className="campaign-card" sx={{ cursor: "pointer" }} onClick={() => onView(campaign._id)}>
      <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
          {/* Left: Name + Chips */}
          <Box sx={{ flex: "1 1 250px", minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={600} noWrap>{campaign.name}</Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              <Chip label={campaign.type.toUpperCase()} color={typeColors[campaign.type]} size="small" />
              <Chip label={campaign.status.toUpperCase()} color={statusColors[campaign.status]} size="small" />
            </Stack>
          </Box>

          {/* Center: Stats */}
          <Box sx={{ display: "flex", gap: 3, flex: "0 0 auto" }}>
            {[["Target", targetCount], ["Sent", campaign.stats?.totalSent || 0], ["Opened", campaign.stats?.totalOpened || 0], ["Rate", `${rate}%`]].map(([label, val]) => (
              <Box key={label} sx={{ textAlign: "center", minWidth: 50 }}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="body2" fontWeight={600}>{val.toLocaleString?.() || val}</Typography>
              </Box>
            ))}
          </Box>

          {/* Right: Date + Actions */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: "0 0 auto" }}>
            <Typography variant="caption" color="text.secondary">{new Date(campaign.createdAt).toLocaleDateString("en-US", { timeZone: "Asia/Dhaka" })}</Typography>
            <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
              <Tooltip title="View"><IconButton size="small" onClick={() => onView(campaign._id)}><Visibility fontSize="small" /></IconButton></Tooltip>
              {campaign.status === "draft" && (
                <>
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => onEdit(campaign._id)}><Edit fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Execute"><IconButton size="small" color="success" onClick={() => onExecute(campaign._id)}><Send fontSize="small" /></IconButton></Tooltip>
                </>
              )}
              {campaign.status !== "running" && (
                <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => onDelete(campaign._id)}><Delete fontSize="small" /></IconButton></Tooltip>
              )}
            </Stack>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// Loading Spinner
export const Loading = ({ text = "Loading..." }) => (
  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8 }}>
    <CircularProgress />
    <Typography variant="body2" sx={{ mt: 2 }}>{text}</Typography>
  </Box>
);

// Empty State
export const EmptyState = ({ icon: Icon = CampaignIcon, title, subtitle, action }) => (
  <Card>
    <CardContent sx={{ textAlign: "center", py: 6 }}>
      <Icon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
      <Typography variant="h6" color="text.secondary">{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>{subtitle}</Typography>
      {action}
    </CardContent>
  </Card>
);

// Section Card
export const Section = ({ title, children, ...props }) => (
  <Card sx={{ mb: 3 }} {...props}>
    <CardContent>
      {title && <Typography variant="h6" gutterBottom>{title}</Typography>}
      {children}
    </CardContent>
  </Card>
);

// Stats Display (for details page)
export const StatsDisplay = ({ stats, type }) => {
  // Use totalTargeted after execution, fall back to estimatedReach for drafts
  const targetCount = stats?.totalTargeted || stats?.estimatedReach || 0;

  return (
  <Grid container spacing={2} sx={{ mb: 3 }}>
    {[
      ["Target Audience", targetCount],
      ["Total Sent", stats?.totalSent || 0],
      ["Opened", stats?.totalOpened || 0, `Open Rate: ${calcRate(stats?.totalOpened, stats?.totalSent)}%`],
      ["Failed", stats?.totalFailed || 0, `Failure Rate: ${calcRate(stats?.totalFailed, stats?.totalSent)}%`]
    ].map(([label, value, sub]) => (
      <Grid item xs={6} md={3} key={label}>
        <Card>
          <CardContent>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="h4">{value.toLocaleString?.() || value}</Typography>
            {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);
};

// Channel Stats
export const ChannelStats = ({ title, sent, delivered, opened, failed }) => (
  <Section title={title}>
    <Grid container spacing={2}>
      {[["Sent", sent], ["Delivered", delivered], ["Opened", opened], ["Failed", failed]].map(([l, v]) => (
        <Grid item xs={6} sm={3} key={l}>
          <Typography variant="body2" color="text.secondary">{l}</Typography>
          <Typography variant="h6">{(v || 0).toLocaleString()}</Typography>
        </Grid>
      ))}
    </Grid>
  </Section>
);

// Message Preview
export const MessagePreview = ({ type, pushMessage, smsMessage }) => (
  <Section title="Message Content">
    {(type === "push" || type === "both") && pushMessage && (
      <Box sx={{ mb: type === "both" ? 3 : 0 }}>
        <Typography variant="subtitle2" gutterBottom>Push Notification</Typography>
        <Box sx={{ bgcolor: "grey.100", p: 2, borderRadius: 1 }}>
          <Typography variant="subtitle2" color="primary">{pushMessage.title}</Typography>
          <Typography variant="body2">{pushMessage.body}</Typography>
        </Box>
        {(pushMessage.redirectUrlWeb || pushMessage.redirectUrlMobile) && (
          <Box sx={{ mt: 1, p: 1, bgcolor: "info.light", borderRadius: 1 }}>
            <Typography variant="caption" display="block">Web: {pushMessage.redirectUrlWeb || "(default)"}</Typography>
            <Typography variant="caption" display="block">Mobile: {pushMessage.redirectUrlMobile || "(default)"}</Typography>
          </Box>
        )}
      </Box>
    )}
    {(type === "sms" || type === "both") && smsMessage && (
      <Box>
        <Typography variant="subtitle2" gutterBottom>SMS Message</Typography>
        <Box sx={{ bgcolor: "grey.100", p: 2, borderRadius: 1 }}>
          <Typography variant="body2">{smsMessage.content}</Typography>
          <Typography variant="caption" color="text.secondary">{smsMessage.content?.length || 0}/160</Typography>
        </Box>
      </Box>
    )}
  </Section>
);

// Target Audience Display
export const TargetAudienceDisplay = ({ targetAudience }) => {
  const { filterType, customFilters, selectedUserIds } = targetAudience || {};

  return (
    <Section title="Target Audience">
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Filter Type: {filterType === "all" ? "All Users" : filterType === "custom" ? "Custom Filters" : "Select Users"}
      </Typography>

      {filterType === "custom" && customFilters && (
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {customFilters.gender && customFilters.gender !== "all" && (
            <Grid item xs={6} sm={4}>
              <Typography variant="caption" color="text.secondary">Gender</Typography>
              <Typography variant="body2">{customFilters.gender}</Typography>
            </Grid>
          )}
          {customFilters.ageRange && (
            <Grid item xs={6} sm={4}>
              <Typography variant="caption" color="text.secondary">Age Range</Typography>
              <Typography variant="body2">{customFilters.ageRange.min} - {customFilters.ageRange.max}</Typography>
            </Grid>
          )}
          {customFilters.lastActiveWithin && (
            <Grid item xs={6} sm={4}>
              <Typography variant="caption" color="text.secondary">Last Active</Typography>
              <Typography variant="body2">{customFilters.lastActiveWithin} days</Typography>
            </Grid>
          )}
        </Grid>
      )}

      {filterType === "selected" && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="subtitle2">Selected Users ({selectedUserIds?.length || 0})</Typography>
          {selectedUserIds?.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              IDs: {selectedUserIds.slice(0, 5).join(", ")}{selectedUserIds.length > 5 && "..."}
            </Typography>
          )}
        </Box>
      )}
    </Section>
  );
};
